/**
 * ZenMarkup Privacy Analytics — Pie Chart module.
 * Canvas donut chart for new vs returning visitors
 * with colored legend, counts, and percentages.
 */

import * as animate from './animate.js';
import * as pieInteract from './pie-chart-interact.js';

let canvas, ctx;
let hasAnimated = false, activeAnim = null;
const CENTER_COLOR = '#FFFFFF';
const LABEL_COLOR = '#64748B';
const FONT = '12px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const COLORS = { new: '#2563EB', returning: '#CBD5E1' };

/** @param {Object} data { new: int, returning: int } */
export function render(data) {
	canvas = document.querySelector('[data-el="pie-canvas"]');
	if (!canvas) return;
	ctx = canvas.getContext('2d');
	const total = (data.new || 0) + (data.returning || 0);
	if (!hasAnimated && total > 0) {
		pieInteract.drawLegend(data, total, true);
		animateEntrance(data, total);
	} else {
		drawDonut(data, total);
		pieInteract.drawLegend(data, total, false);
	}
	pieInteract.bindTouch(canvas, data, total);
}

function drawDonut(data, total, progress) {
	const dpr = Math.ceil(window.devicePixelRatio || 1);
	const size = 140;
	canvas.width = size * dpr;
	canvas.height = size * dpr;
	canvas.style.width = size + 'px';
	canvas.style.height = size + 'px';
	ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
	ctx.clearRect(0, 0, size, size);

	const cx = size / 2, cy = size / 2;
	const outerR = 63, innerR = 40;

	if (total === 0) {
		ctx.beginPath();
		ctx.arc(cx, cy, outerR, 0, Math.PI * 2);
		ctx.arc(cx, cy, innerR, 0, Math.PI * 2, true);
		ctx.fillStyle = '#E2E8F0';
		ctx.fill();
		ctx.fillStyle = LABEL_COLOR;
		ctx.font = '13px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		ctx.fillText('No data', cx, cy);
		return;
	}

	const p = typeof progress === 'number' ? progress : 1;
	let startAngle = -Math.PI / 2;
	const segments = [
		{ key: 'new', value: data.new || 0 },
		{ key: 'returning', value: data.returning || 0 }
	];

	segments.forEach(function (seg) {
		if (seg.value === 0) return;
		const sweep = (seg.value / total) * Math.PI * 2 * p;
		ctx.beginPath();
		ctx.arc(cx, cy, outerR, startAngle, startAngle + sweep);
		ctx.arc(cx, cy, innerR, startAngle + sweep, startAngle, true);
		ctx.closePath();
		ctx.fillStyle = COLORS[seg.key];
		ctx.fill();
		startAngle += sweep;
	});

	ctx.beginPath();
	ctx.arc(cx, cy, innerR, 0, Math.PI * 2);
	ctx.fillStyle = CENTER_COLOR;
	ctx.fill();

	var textAlpha = 1;
	if (p < 0.7) {
		textAlpha = 0;
	} else if (p < 1) {
		textAlpha = (p - 0.7) / 0.3;
	}
	if (textAlpha > 0) {
		ctx.save();
		ctx.globalAlpha = textAlpha;
		ctx.fillStyle = '#0F172A';
		ctx.font = 'bold 22px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
		ctx.textAlign = 'center';
		ctx.textBaseline = 'middle';
		ctx.fillText(total.toString(), cx, cy - 6);
		ctx.fillStyle = LABEL_COLOR;
		ctx.font = FONT;
		ctx.fillText('Total', cx, cy + 14);
		ctx.restore();
	}
}

function animateEntrance(data, total) {
	if (activeAnim) {
		activeAnim.cancel();
	}
	activeAnim = animate.run(function (progress) {
		drawDonut(data, total, progress);
		if (progress >= 1) {
			activeAnim = null;
			hasAnimated = true;
			pieInteract.showLegend();
		}
	}, 900);
}
